import { BarChart3, Calendar, ChevronDown, ChevronUp, TrendingUp } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import { Progress } from "@/components/ui/progress";
import type { CustomerAnalyticsQuery } from "@/gql/graphql";

interface CustomerSeasonalPatternsCardProps {
  analytics?: NonNullable<CustomerAnalyticsQuery["customerAnalytics"]>;
  loading?: boolean;
}

export function CustomerSeasonalPatternsCard({
  analytics,
  loading = false,
}: CustomerSeasonalPatternsCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (loading || !analytics) {
    return <SeasonalPatternsSkeleton />;
  }

  const { seasonalPatterns } = analytics;

  const getSeasonalityLabel = (score: number) => {
    if (score >= 70)
      return {
        label: "Highly Seasonal",
        color:
          "bg-purple-100 text-purple-900 border-purple-300 dark:bg-purple-950 dark:text-purple-100",
      };
    if (score >= 40)
      return {
        label: "Moderately Seasonal",
        color: "bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-950 dark:text-blue-100",
      };
    if (score >= 20)
      return {
        label: "Slightly Seasonal",
        color: "bg-green-100 text-green-900 border-green-300 dark:bg-green-950 dark:text-green-100",
      };
    return {
      label: "Consistent",
      color: "bg-gray-100 text-gray-900 border-gray-300 dark:bg-gray-950 dark:text-gray-100",
    };
  };

  const seasonalityInfo = getSeasonalityLabel(seasonalPatterns.seasonalityScore);

  const getDayColor = (percentage: number) => {
    if (percentage >= 20) return "bg-emerald-500";
    if (percentage >= 15) return "bg-green-500";
    if (percentage >= 10) return "bg-yellow-500";
    return "bg-gray-400";
  };

  const sortedDays = [...seasonalPatterns.dayOfWeekPatterns].sort(
    (a, b) => b.percentageOfVisits - a.percentageOfVisits
  );
  const sortedMonths = [...seasonalPatterns.monthlyTrends].sort(
    (a, b) => b.visitCount - a.visitCount
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            Seasonal Patterns
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-muted-foreground hover:text-foreground"
          >
            {isExpanded ? (
              <>
                <span className="mr-1">Collapse</span>
                <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                <span className="mr-1">Expand</span>
                <ChevronDown className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Collapsed State - Key insights */}
        {!isExpanded ? (
          <>
            {/* Seasonality Score */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/20 dark:to-indigo-950/20 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                  Seasonality Score
                </span>
                <Badge variant="outline" className={seasonalityInfo.color}>
                  {seasonalityInfo.label}
                </Badge>
              </div>
              <div className="mb-2">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-purple-700 dark:text-purple-300">Behavior Consistency</span>
                  <span className="font-medium">
                    {seasonalPatterns.seasonalityScore.toFixed(0)}%
                  </span>
                </div>
                <Progress
                  value={seasonalPatterns.seasonalityScore}
                  className="h-2 bg-purple-100 dark:bg-purple-950"
                />
              </div>
            </div>

            {/* Quick Insights */}
            <div className="bg-muted p-3 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">Peak Day</div>
                  <div className="font-medium text-emerald-600 dark:text-emerald-400">
                    {seasonalPatterns.peakDayOfWeek}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Peak Month</div>
                  <div className="font-medium text-emerald-600 dark:text-emerald-400">
                    {seasonalPatterns.peakMonth}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Expanded State - Detailed analysis */}

            {/* Seasonality Analysis */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Seasonality Analysis
              </h4>
              <div className="border rounded-lg p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/20 dark:to-indigo-950/20">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Behavior Type</div>
                    <Badge variant="outline" className={seasonalityInfo.color}>
                      {seasonalityInfo.label}
                    </Badge>
                    <div className="text-xs text-muted-foreground mt-1">
                      Seasonality Score: {seasonalPatterns.seasonalityScore.toFixed(1)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Peak Activity</div>
                    <div className="font-semibold text-emerald-600 dark:text-emerald-400">
                      {seasonalPatterns.peakDayOfWeek}s
                    </div>
                    <div className="text-xs text-muted-foreground">Most preferred day</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Best Month</div>
                    <div className="font-semibold text-emerald-600 dark:text-emerald-400">
                      {seasonalPatterns.peakMonth}
                    </div>
                    <div className="text-xs text-muted-foreground">Highest activity</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Day of Week Patterns */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Weekly Patterns
              </h4>
              <div className="space-y-2">
                {sortedDays.map((day) => (
                  <div
                    key={day.dayName}
                    className="flex items-center justify-between p-2 border rounded"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-3 h-3 rounded-full ${getDayColor(day.percentageOfVisits)}`}
                      />
                      <span className="font-medium">{day.dayName}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-muted-foreground">{day.visitCount} visits</span>
                      <span className="font-medium min-w-[3rem] text-right">
                        {day.percentageOfVisits.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Monthly Trends */}
            {sortedMonths.length > 0 && (
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Monthly Trends (Last 12 Months)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {sortedMonths.slice(0, 6).map((month) => (
                    <div key={`${month.year}-${month.month}`} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium text-sm">
                          {month.monthName} {month.year}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {month.visitCount} visits
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Total Spent:</span>
                          <span className="font-medium text-green-600 dark:text-green-400">
                            {<PriceDisplay amount={month.totalSpent} />}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Avg per Visit:</span>
                          <span>{<PriceDisplay amount={month.avgSpentPerVisit} />}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Insights and Recommendations */}
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-4 rounded-lg">
              <h4 className="font-medium mb-2 flex items-center gap-2 text-amber-900 dark:text-amber-100">
                <TrendingUp className="w-4 h-4" />
                Strategic Insights
              </h4>
              <div className="text-sm text-amber-800 dark:text-amber-200 space-y-2">
                {seasonalPatterns.seasonalityScore >= 40 ? (
                  <>
                    <p>• This customer shows strong seasonal patterns in their booking behavior</p>
                    <p>
                      • Consider targeted promotions during peak periods (
                      {seasonalPatterns.peakMonth})
                    </p>
                    <p>
                      • Schedule availability around preferred day ({seasonalPatterns.peakDayOfWeek}
                      )
                    </p>
                  </>
                ) : (
                  <>
                    <p>• This customer has consistent booking patterns throughout the year</p>
                    <p>• Reliable for steady revenue planning</p>
                    <p>• Good candidate for membership or package deals</p>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SeasonalPatternsSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-48" />
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
          <div className="grid grid-cols-2 gap-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
